import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import HeaderBox from "components/navbar/HeaderBox";
import {
  Assessment,
  AssessmentStatus,
  OrganizationAssessment,
  ResponderGrade,
  Responding,
} from "types/assessmentTypes";
import { CgProfile } from "react-icons/cg";
import {
  HiOutlineBuildingOffice2,
  HiOutlinePaperAirplane,
} from "react-icons/hi2";
import { motion, AnimatePresence } from "framer-motion";
import DynamicForm, { Field } from "components/dynamic/DynamicForm";
import { useModal } from "hooks/useModal";
import { FieldValues } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "reduxFiles/hooks";
import {
  useGetQuestionsQuery,
  useGetResponderAssessmentsQuery,
  useGetOrganizationAssessmentsForResponderQuery,
  useGetRuleCriteriaQuery,
  useSetAnswersMutation,
  useUpdateResponderProfileMutation,
  useSetOrganizationAnswersMutation,
} from "reduxFiles/api/responderApi";
import { addNotification } from "reduxFiles/slices/notificationSlice";
import { setResponder } from "reduxFiles/slices/responderAuthSlice";
import Sidebar from "./Sidebar";
import Accordion from "./Accordion";
import ConfirmModal from "components/modal/ConfirmModal";
import Button from "components/button/Button";
import { enumToOptions } from "services/helper";
import { useLazyGetMajorsQuery } from "reduxFiles/api/userApi";

const PAGE_SIZE = 20;

interface StoredAnswer {
  questionId: number;
  optionId: number;
}

type AssessmentType = "company" | "organization";

const AssessmentPage = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const dispatch = useAppDispatch();
  const responder = useAppSelector((s) => s.responderAuth.responder);
  const modal = useModal();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [forceProfile, setForceProfile] = useState(false);
  const [assessmentType, setAssessmentType] =
    useState<AssessmentType>("company");

  // Profile validation
  const isProfileComplete = useMemo(() => {
    if (!responder) return false;

    const hasMajor = responder.majorId && responder.majorId !== -1;
    const hasOtherMajor =
      responder.majorOther && responder.majorOther.trim() !== "";

    return !!(
      responder.name &&
      responder.mobile &&
      responder.email &&
      responder.nationalCode &&
      responder.degree &&
      (hasMajor || hasOtherMajor) &&
      responder.yearsOfExperience &&
      responder.expertise
    );
  }, [responder]);

  useEffect(() => {
    if (!isProfileComplete && !forceProfile) {
      setForceProfile(true);
      modal.openModal();
    }
  }, [isProfileComplete, forceProfile, modal, responder]);

  const [updateProfileApi, { isLoading: isUpdateProfile }] =
    useUpdateResponderProfileMutation();

  const handleSubmitProfile = useCallback(
    async (formData: FieldValues) => {
      try {
        const body = {
          ...formData,
          majorId: formData.majorId === -1 ? null : formData.majorId,
          majorOther: formData.majorId === -1 ? formData.majorOther : "",
        };

        const data = await updateProfileApi(body).unwrap();
        dispatch(setResponder(data.data));
        setForceProfile(false);
        modal.closeModal();
      } catch (err) {
        console.error(err);
      }
    },
    [updateProfileApi, dispatch, modal]
  );

  // ============= COMPANY ASSESSMENTS =============
  const [companyPage, setCompanyPage] = useState(1);
  const [allCompanyAssessments, setAllCompanyAssessments] = useState<
    Assessment[]
  >([]);

  const {
    data: companyAssessmentsData,
    isFetching: isFetchingCompany,
    refetch: refetchCompany,
  } = useGetResponderAssessmentsQuery({
    page: companyPage,
    limit: PAGE_SIZE,
  });

  const hasMoreCompany = (companyAssessmentsData?.pageCount ?? 0) > companyPage;

  useEffect(() => {
    if (!companyAssessmentsData?.items) return;

    const uniqueItems = companyAssessmentsData.items.filter(
      (item, index, self) => self.findIndex((i) => i.id === item.id) === index
    );

    setAllCompanyAssessments((prev) =>
      companyPage === 1
        ? uniqueItems
        : [...prev, ...uniqueItems].filter(
            (item, index, self) =>
              self.findIndex((i) => i.id === item.id) === index
          )
    );
  }, [companyAssessmentsData, companyPage]);

  // ============= ORGANIZATION ASSESSMENTS =============
  const [orgPage, setOrgPage] = useState(1);
  const [allOrgAssessments, setAllOrgAssessments] = useState<
    OrganizationAssessment[]
  >([]);

  const {
    data: orgAssessmentsData,
    isFetching: isFetchingOrg,
    refetch: refetchOrg,
  } = useGetOrganizationAssessmentsForResponderQuery({
    page: orgPage,
    limit: PAGE_SIZE,
  });

  const hasMoreOrg = (orgAssessmentsData?.pageCount ?? 0) > orgPage;

  useEffect(() => {
    if (!orgAssessmentsData?.items) return;

    const uniqueItems = orgAssessmentsData.items.filter(
      (item, index, self) => self.findIndex((i) => i.id === item.id) === index
    );

    setAllOrgAssessments((prev) =>
      orgPage === 1
        ? uniqueItems
        : [...prev, ...uniqueItems].filter(
            (item, index, self) =>
              self.findIndex((i) => i.id === item.id) === index
          )
    );
  }, [orgAssessmentsData, orgPage]);

  const hasOrganizationAssessments = allOrgAssessments.length > 0;

  // Selection state
  const [selectedAssessmentId, setSelectedAssessmentId] = useState<
    number | null
  >(null);
  const [selectedResponding, setSelectedResponding] =
    useState<Responding | null>(null);
  const [selectedOrgAssessment, setSelectedOrgAssessment] =
    useState<OrganizationAssessment | null>(null);

  const selectedAssessment = useMemo(() => {
    if (assessmentType === "company") {
      return allCompanyAssessments.find((a) => a.id === selectedAssessmentId);
    } else {
      return allOrgAssessments.find((a) => a.id === selectedAssessmentId);
    }
  }, [
    assessmentType,
    allCompanyAssessments,
    allOrgAssessments,
    selectedAssessmentId,
  ]);

  const handleSelectResponding = useCallback(
    (
      assessmentId: number,
      responding: Responding,
      type: AssessmentType = "company"
    ) => {
      setAssessmentType(type);
      setSelectedAssessmentId(assessmentId);
      setSelectedResponding(responding);
      setSelectedOrgAssessment(null);
      setIsSidebarOpen(false);
    },
    []
  );

  const handleSelectOrganizationAssessment = useCallback(
    (assessment: OrganizationAssessment) => {
      setAssessmentType("organization");
      setSelectedAssessmentId(assessment.id);
      setSelectedOrgAssessment(assessment);
      setSelectedResponding(null);
      setIsSidebarOpen(false);
    },
    []
  );

  // ============= COMPANY QUESTIONS =============
  const { data: ruleCriteria, isLoading: isRuleCriteriaLoading } =
    useGetRuleCriteriaQuery(
      {
        filters: {
          companyStage: selectedAssessment?.company?.companyStage ?? undefined,
          groupType: !selectedAssessment?.company?.companyStage
            ? selectedAssessment?.company?.totalStaffCount?.companySize
            : undefined,
        },
      },
      {
        skip:
          assessmentType !== "company" ||
          !selectedResponding?.groupId ||
          selectedResponding?.isAnswered === true,
      }
    );

  const criteriaId = useMemo(() => {
    if (!ruleCriteria || ruleCriteria.length === 0) return [];
    const ids = ruleCriteria
      .map((criteria) => criteria.criteriaId)
      .filter(Boolean);
    return [...new Set(ids)];
  }, [ruleCriteria]);

  const { data: companyQuestionsData, isFetching: isCompanyQuestionsFetching } =
    useGetQuestionsQuery(
      {
        filters: {
          groupId: selectedResponding?.groupId,
          criteriaId: criteriaId.length > 0 ? criteriaId : undefined,
        },
      },
      {
        skip:
          assessmentType !== "company" ||
          !selectedResponding?.groupId ||
          selectedResponding?.isAnswered === true ||
          isRuleCriteriaLoading,
        refetchOnMountOrArgChange: true,
      }
    );

  // ============= ORGANIZATION QUESTIONS =============
  const orgAccordionData = useMemo(() => {
    if (assessmentType !== "organization" || !selectedOrgAssessment) return [];

    const items = selectedOrgAssessment.organization?.form?.items || [];

    // Group by 'group' field
    const map = new Map<string, { group: string; questions: any[] }>();

    items.forEach((question) => {
      const groupKey = question.group || t("assessment.uncategorized");
      if (!map.has(groupKey)) {
        map.set(groupKey, {
          group: groupKey,
          questions: [],
        });
      }
      map.get(groupKey)!.questions.push(question);
    });

    // Convert to Accordion format
    return Array.from(map.values()).map((group, idx) => ({
      id: idx + 1,
      title: group.group,
      index: idx + 1,
      rowIndex: idx + 1,
      questions: group.questions.sort(
        (a, b) => (a.index || 0) - (b.index || 0)
      ),
    }));
  }, [assessmentType, selectedOrgAssessment, t]);

  // ============= GROUP QUESTIONS =============
  const groupedCompanyQuestions = useMemo(() => {
    const data = companyQuestionsData;
    if (!data) return [];

    const map = new Map<
      number,
      {
        id: number;
        title: string;
        rowIndex: number;
        index: number;
        questions: any[];
      }
    >();

    data.forEach((question, qindex) => {
      const criteriaId = question.criteria?.id || 0;
      if (!map.has(criteriaId)) {
        map.set(criteriaId, {
          id: criteriaId,
          index: question.criteria?.index || 0,
          rowIndex: 0,
          title: question.criteria?.name || t("assessment.uncategorized"),
          questions: [],
        });
      }
      map.get(criteriaId)!.questions.push(question);
    });

    return Array.from(map.values())
      .sort((a, b) => a.index - b.index)
      .map((group, idx) => ({
        ...group,
        rowIndex: idx + 1,
        questions: group.questions.sort(
          (a, b) => (a.order || 0) - (b.order || 0)
        ),
      }));
  }, [companyQuestionsData, t]);

  const accordionData =
    assessmentType === "company" ? groupedCompanyQuestions : orgAccordionData;

  const isOrgAssessmentAnswered = useMemo(() => {
    if (!selectedOrgAssessment) return false;
    return (
      selectedOrgAssessment.status === AssessmentStatus.COMPLETED ||
      selectedOrgAssessment.answers?.some(
        (answer) => answer.responding?.responder?.id === responder?.id
      )
    );
  }, [responder?.id, selectedOrgAssessment]);

  const isLoading =
    (assessmentType === "company" &&
      (isRuleCriteriaLoading || isCompanyQuestionsFetching)) ||
    (assessmentType === "organization" && false);

  // ========== ANSWERS STATE ==========
  const [answersMap, setAnswersMap] = useState<Map<number, number>>(new Map());
  const [descriptionsMap, setDescriptionsMap] = useState<Map<number, string>>(
    new Map()
  );
  const storageKey = `assessment-${assessmentType}-${selectedAssessmentId}-${
    selectedResponding?.groupId || selectedOrgAssessment?.id
  }`;
  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    if (!selectedAssessmentId) {
      setAnswersMap(new Map());
      setDescriptionsMap(new Map());
      return;
    }

    isInitialLoadRef.current = true;

    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        const parsed: StoredAnswer[] = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          const newMap = new Map<number, number>();
          parsed.forEach((answer) => {
            newMap.set(answer.questionId, answer.optionId);
          });
          setAnswersMap(newMap);
        } else {
          setAnswersMap(new Map());
        }
      } catch (e) {
        console.error(e);
        setAnswersMap(new Map());
      }
    } else {
      setAnswersMap(new Map());
    }

    // Reset descriptions when changing assessment
    setDescriptionsMap(new Map());

    isInitialLoadRef.current = false;
  }, [selectedAssessmentId, storageKey]);

  const saveTimeoutRef = useRef<any | null>(null);

  useEffect(() => {
    if (isInitialLoadRef.current) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      if (selectedAssessmentId) {
        const toStore: StoredAnswer[] = Array.from(answersMap.entries()).map(
          ([questionId, optionId]) => ({ questionId, optionId })
        );

        if (toStore.length > 0) {
          localStorage.setItem(storageKey, JSON.stringify(toStore));
        } else {
          localStorage.removeItem(storageKey);
        }
      }
    }, 500);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [answersMap, storageKey, selectedAssessmentId]);

  const updateAnswer = useCallback(
    (questionId: number, optionId: number | null) => {
      setAnswersMap((prev) => {
        const newMap = new Map(prev);
        if (optionId === null) {
          newMap.delete(questionId);
        } else {
          newMap.set(questionId, optionId);
        }
        return newMap;
      });
    },
    []
  );

  const updateDescription = useCallback(
    (questionId: number, description: string) => {
      setDescriptionsMap((prev) => new Map(prev).set(questionId, description));
    },
    []
  );

  // Get total questions count for progress
  const getTotalQuestionsCount = useCallback((): number => {
    let total = 0;
    accordionData.forEach((group) => {
      total += group.questions.length;
    });
    return total;
  }, [accordionData]);

  const getProgressPercentage = useCallback((): number => {
    const total = getTotalQuestionsCount();
    if (total === 0) return 0;
    const answered = answersMap.size;
    return Math.round((answered / total) * 100);
  }, [answersMap, getTotalQuestionsCount]);

  // Validation
  const validateAllQuestionsAnswered = useCallback((): boolean => {
    if (!accordionData.length) return false;

    const allQuestionIds: number[] = [];
    accordionData.forEach((group) => {
      group.questions.forEach((q: any) => {
        allQuestionIds.push(q.id);
      });
    });

    const missingQuestions = allQuestionIds.filter((id) => !answersMap.has(id));

    if (missingQuestions.length > 0) {
      dispatch(
        addNotification({
          type: "error",
          title: t("assessment.incompleteAnswers"),
          message: t("assessment.pleaseAnswerAllQuestions", {
            count: missingQuestions.length,
          }),
        })
      );
      return false;
    }
    return true;
  }, [accordionData, answersMap, dispatch, t]);

  // Submit answers
  const confirmModal = useModal();
  const [setAnswerApi, { isLoading: isSetAnswerLoading }] =
    useSetAnswersMutation();
  const [setOrganizationAnswers, { isLoading: isSetOrgAnswerLoading }] =
    useSetOrganizationAnswersMutation();

  const handleSubmitAnswers = useCallback(async () => {
    if (!selectedAssessmentId) {
      dispatch(
        addNotification({
          type: "error",
          message: t("assessment.noGroupSelected"),
        })
      );
      confirmModal.closeModal();
      return;
    }

    if (!validateAllQuestionsAnswered()) {
      confirmModal.closeModal();
      return;
    }

    if (assessmentType === "company") {
      const optionIds = Array.from(answersMap.values()).filter(
        (value): value is number => value != null
      );

      try {
        const payload: any = {
          assessmentId: selectedAssessmentId,
          data: optionIds,
        };

        if (selectedResponding?.id) {
          payload.respondingId = selectedResponding.id;
        }

        await setAnswerApi(payload).unwrap();

        localStorage.removeItem(storageKey);
        confirmModal.closeModal();

        setSelectedResponding(null);
        setSelectedOrgAssessment(null);
        setSelectedAssessmentId(null);

        refetchCompany();
      } catch (err: any) {
        console.error("Error submitting answers:", err);
      }
    } else {
      // Organization assessment
      const orgRespondingId = selectedOrgAssessment?.responding?.id;
      if (!orgRespondingId) {
        dispatch(
          addNotification({
            type: "error",
            message: t("assessment.missingRespondingId"),
          })
        );
        confirmModal.closeModal();
        return;
      }

      const answers = Array.from(answersMap.entries()).map(
        ([questionId, optionId]) => ({
          questionId,
          optionId,
          description: descriptionsMap.get(questionId) || "",
        })
      );

      try {
        await setOrganizationAnswers({
          organizationAssessmentId: selectedAssessmentId,
          organizationRespondingId: orgRespondingId,
          answers,
        }).unwrap();

        localStorage.removeItem(storageKey);
        confirmModal.closeModal();

        setSelectedResponding(null);
        setSelectedOrgAssessment(null);
        setSelectedAssessmentId(null);

        refetchOrg();
      } catch (err: any) {
        console.error("Error submitting organization answers:", err);
      }
    }
  }, [
    selectedAssessmentId,
    selectedResponding?.id,
    selectedOrgAssessment,
    assessmentType,
    validateAllQuestionsAnswered,
    answersMap,
    descriptionsMap,
    dispatch,
    t,
    setAnswerApi,
    setOrganizationAnswers,
    storageKey,
    confirmModal,
    refetchCompany,
    refetchOrg,
  ]);

  // search major
  const [triggerMajors] = useLazyGetMajorsQuery();
  const searchMajors = async (text: string) => {
    try {
      const res = await triggerMajors({
        page: 1,
        limit: 50,
        filters: { name: text },
      }).unwrap();

      return res.items?.map((u) => ({
        label: u.meta?.translations?.[lang] || u.name,
        value: u.id,
      }));
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  // Profile fields
  const fields: Field[] = useMemo(
    () => [
      {
        name: "name",
        label: t("user.fullname"),
        type: "text",
        formed: true,
        required: true,
      },
      {
        name: "nationalCode",
        label: t("responder.nationalCode"),
        type: "text",
        formed: true,
        required: true,
        disabled: true,
      },
      {
        name: "mobile",
        label: t("user.mobile"),
        type: "mobile",
        formed: true,
        required: true,
      },
      {
        name: "email",
        label: t("user.email"),
        type: "email",
        formed: true,
        required: true,
      },
      {
        name: "degree",
        label: t("responder.degree"),
        type: "select",
        options: enumToOptions(ResponderGrade, t, "responder.degrees"),
        formed: true,
        filtered: true,
        required: true,
      },
      {
        name: "degreesOther",
        label: t("responder.degreesOther"),
        type: "text",
        formed: true,
        required: true,
        showWhen: {
          field: "degree",
          equals: "OTHER",
        },
      },
      {
        name: "majorId",
        label: t("responder.majorId"),
        type: "select",
        onSearch: searchMajors,
        formed: true,
        required: true,
        defaultValue: {
          default: {
            label:
              responder?.major?.meta?.translations?.[lang] ||
              responder?.major?.name,
            value: responder?.major?.id,
          },
        },
        other: true,
      },
      {
        name: "majorOther",
        label: t("responder.majorOther"),
        type: "text",
        formed: true,
        required: true,
        showWhen: {
          field: "majorId",
          equals: -1,
        },
      },
      {
        name: "yearsOfExperience",
        label: t("responder.yearsOfExperience"),
        type: "number",
        formed: true,
        required: true,
      },
      {
        name: "expertise",
        label: t("responder.expertise"),
        type: "textarea",
        formed: true,
        required: true,
        rows: 3,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleLoadMoreAssessments = useCallback(() => {
    if (assessmentType === "company") {
      setCompanyPage((prev) => prev + 1);
    } else {
      setOrgPage((prev) => prev + 1);
    }
  }, [assessmentType]);

  if (forceProfile) {
    return (
      <DynamicForm
        open={modal.isOpen}
        onClose={() => {
          if (!isProfileComplete) {
            dispatch(
              addNotification({
                type: "error",
                title: t("assessment.profileComplete"),
                message: t("assessment.invalidProfileText"),
              })
            );
          } else {
            modal.closeModal();
            setForceProfile(false);
          }
        }}
        onSubmit={handleSubmitProfile}
        fields={fields}
        defaultValues={responder || {}}
        title={t("assessment.profileComplete")}
        isEdit
        loading={isUpdateProfile}
      />
    );
  }

  const HeaderComponent = () => {
    const isAnswered =
      assessmentType === "company"
        ? selectedResponding?.isAnswered
        : isOrgAssessmentAnswered;

    const title = selectedAssessment?.company?.name;

    const groupName =
      assessmentType === "company"
        ? t(`assessmentGroups.${selectedResponding?.group?.key}`)
        : t("assessment.organizationAssessment");

    return (
      <div className="flex-shrink-0 border-b border-gray-200 px-5 py-4 dark:border-white/10">
        <div className="flex flex-col flex-wrap gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-800 dark:text-white">
              {title || t("assessment.selectAssessment")}
            </h1>
            {selectedResponding && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t("assessment.selectedGroup")} :{" "}
                <span className="font-medium text-brand-600 dark:text-brand-200">
                  {groupName}
                </span>
              </p>
            )}
            {selectedOrgAssessment && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t("assessment.organization")} :{" "}
                <span className="font-medium text-brand-600 dark:text-brand-200">
                  {selectedOrgAssessment.organization?.name +
                    " " +
                    selectedOrgAssessment.organization?.province?.name || ""}
                </span>
              </p>
            )}
          </div>

          {!isAnswered && accordionData.length > 0 && (
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex flex-col items-center gap-2">
                <div className="flex items-center gap-2">
                  <div className="relative h-2 w-32 overflow-hidden rounded-full bg-gray-300 dark:bg-navy-600">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{
                        width: `${getProgressPercentage()}%`,
                      }}
                      transition={{ duration: 0.5 }}
                      className={`h-full rounded-full ${
                        getProgressPercentage() === 100
                          ? "bg-green-500"
                          : "bg-gradient-to-r from-brand-600 to-brand-700 dark:from-brand-200 dark:to-brand-300"
                      }`}
                    />
                  </div>
                  <span className="text-xs font-medium text-gray-800 dark:text-gray-300">
                    {getProgressPercentage()} %
                  </span>
                </div>
                <span className="text-xs font-medium text-gray-800 dark:text-gray-300">
                  {t("assessment.mustAnswerAll")}
                </span>
              </div>

              <Button
                onClick={confirmModal.openModal}
                startIcon={<HiOutlinePaperAirplane className="h-4 w-4" />}
                loading={isSetAnswerLoading || isSetOrgAnswerLoading}
              >
                {t("assessment.submitAnswers")}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const isAnswered =
    assessmentType === "company"
      ? selectedResponding?.isAnswered
      : isOrgAssessmentAnswered;

  const isSidebarLoading = isFetchingCompany || isFetchingOrg;

  return (
    <div className="dark:to-navy-950 flex h-full flex-col bg-gradient-to-br from-gray-50 to-gray-100 dark:from-navy-900">
      <HeaderBox
        title={
          t("assessment.title") +
          " : " +
          (responder?.name || responder?.nationalCode || "")
        }
        secondaryButton={{
          title: t("assessment.profile"),
          handler: () => modal.openModal(),
          icon: <CgProfile />,
        }}
        refreshBtn={() => {
          refetchCompany();
          refetchOrg();
        }}
      />

      {/* Mobile menu button */}
      <div className="block px-4 pt-3 lg:hidden">
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsSidebarOpen(true)}
          className="flex w-full items-center justify-between rounded-2xl bg-gradient-to-r from-brand-500 to-brand-600 px-5 py-3 text-white shadow-lg shadow-brand-500/30"
        >
          <div className="flex items-center gap-2">
            <HiOutlineBuildingOffice2 className="h-5 w-5" />
            <span className="font-medium">{t("assessment.selectGroup")}</span>
          </div>
          {selectedResponding && (
            <span className="rounded-full bg-white/20 px-2 py-1 text-xs">
              {t(`assessmentGroups.${selectedResponding.group?.key}`)}
            </span>
          )}
          {selectedOrgAssessment && (
            <span className="rounded-full bg-white/20 px-2 py-1 text-xs">
              {t("assessment.organization")}
            </span>
          )}
        </motion.button>
      </div>

      <div className="flex flex-1 gap-4 p-4">
        <Sidebar
          companyAssessments={allCompanyAssessments}
          organizationAssessments={allOrgAssessments}
          hasOrganizationAssessments={hasOrganizationAssessments}
          selectedAssessmentId={selectedAssessmentId}
          selectedResponding={selectedResponding}
          selectedOrgAssessment={selectedOrgAssessment}
          onSelectResponding={handleSelectResponding}
          onSelectOrganizationAssessment={handleSelectOrganizationAssessment}
          isMobileOpen={isSidebarOpen}
          onCloseMobile={() => setIsSidebarOpen(false)}
          assessmentType={assessmentType}
          isSidebarLoading={isSidebarLoading}
          responder={responder}
          hasMoreCompany={hasMoreCompany}
          hasMoreOrg={hasMoreOrg}
          isFetchingCompany={isFetchingCompany}
          isFetchingOrg={isFetchingOrg}
          onLoadMore={handleLoadMoreAssessments}
        />

        {/* Content Section */}
        <section className="flex min-w-0 flex-1 flex-col rounded-2xl border border-gray-200/80 bg-white/90 shadow-xl backdrop-blur-sm dark:border-white/10 dark:bg-navy-800/80">
          {/* Header */}
          <HeaderComponent />

          {/* Questions */}
          <div className="custom-scrollbar flex-1 overflow-y-auto p-5">
            <AnimatePresence mode="wait">
              {!selectedAssessmentId ? (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex h-[50vh] items-center justify-center"
                >
                  <div className="text-center">
                    <div className="mb-4 text-7xl">📋</div>
                    <p className="text-gray-500 dark:text-gray-400">
                      {t("assessment.selectGroup")}
                    </p>
                  </div>
                </motion.div>
              ) : isAnswered ? (
                <motion.div
                  key="completed"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex h-[50vh] items-center justify-center"
                >
                  <div className="max-w-md text-center">
                    <div className="mb-6 flex justify-center">
                      <div className="rounded-full bg-green-100 p-4 dark:bg-green-500/20">
                        <svg
                          className="h-16 w-16 text-green-600 dark:text-green-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                    </div>

                    <h2 className="mb-3 text-2xl font-bold text-gray-800 dark:text-white">
                      {t("assessment.alreadyCompleted")}
                    </h2>

                    <p className="mb-2 text-gray-600 dark:text-gray-400">
                      {assessmentType === "company"
                        ? t("assessment.completedForGroup", {
                            groupName: selectedResponding?.group?.name,
                          })
                        : t("assessment.completedForOrganization")}
                    </p>
                  </div>
                </motion.div>
              ) : isLoading ? (
                <div className="flex h-[50vh] items-center justify-center">
                  <div className="mb-4">
                    <div className="loader w-8 text-brand-500" />
                  </div>
                </div>
              ) : accordionData.length === 0 ? (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex h-[50vh] items-center justify-center"
                >
                  <div className="text-center">
                    <div className="mb-4 text-7xl">❓</div>
                    <p className="text-gray-500 dark:text-gray-400">
                      {t("assessment.noQuestions")}
                    </p>
                  </div>
                </motion.div>
              ) : accordionData.length > 0 ? (
                <motion.div
                  key="questions"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="space-y-5"
                >
                  {accordionData.map((group, idx) => (
                    <motion.div
                      key={`group-${group.id}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(idx * 0.03, 0.3) }}
                    >
                      <Accordion
                        data={group}
                        answersMap={answersMap}
                        onAnswerChange={updateAnswer}
                        currentCriteriaId={group.index}
                        isFirst={idx === 0}
                        descriptionsMap={descriptionsMap}
                        onDescriptionChange={updateDescription}
                        type={assessmentType}
                      />
                    </motion.div>
                  ))}
                </motion.div>
              ) : null}
            </AnimatePresence>

            {selectedAssessmentId &&
              !isAnswered &&
              accordionData.length > 0 && <HeaderComponent />}
          </div>
        </section>
      </div>

      {/* Profile Modal */}
      <DynamicForm
        open={modal.isOpen && !forceProfile}
        onClose={modal.closeModal}
        onSubmit={handleSubmitProfile}
        fields={fields}
        defaultValues={
          responder
            ? {
                ...responder,
                majorId: responder.majorOther ? -1 : responder.majorId,
              }
            : {}
        }
        title={t("assessment.profileComplete")}
        isEdit={!!responder}
        loading={isUpdateProfile}
      />

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => {
          confirmModal.closeModal();
        }}
        primary={true}
        handler={handleSubmitAnswers}
        loading={isSetAnswerLoading || isSetOrgAnswerLoading}
        title={t("assessment.confirmAnswers")}
        desc={t("assessment.confirmAnswersDesc")}
      />
    </div>
  );
};

export default AssessmentPage;
